/*
 * Intelligems — custom personalization subtotal support.
 *
 * The personalization flows (pet portrait, desktop portrait, then & now portrait)
 * render a "Subtotal:" element that is NOT a plain variant price: it is
 *
 *     variant price + every paid add-on the customer has ticked
 *
 * Because of that sum, Intelligems cannot swap the text itself — the dashboard
 * price selector deliberately excludes these elements via
 * `.product-price-final:not([data-current-variant-price])`.
 *
 * Instead we ask the Intelligems JS API for the tested price of the currently
 * selected variant and re-add the add-ons on top of it ourselves.
 *
 * Contract with the markup / product.js:
 *   - data-variant-id             → the variant currently selected
 *   - data-current-variant-price  → the theme's own variant price, in cents (fallback)
 *   - data-upsells-total-price    → sum of the selected add-ons, in cents
 */
(function () {
    var SUBTOTAL_SELECTOR = '.product-price-final[data-current-variant-price]'

    /**
     * "29.95" -> 2995. Returns null for anything that isn't a usable amount
     * (null, undefined, "", garbage), which is how callers detect "no test price".
     * Note 0 is a valid tested price, so only null/NaN may fall through.
     */
    function toCents (currencyValue) {
        if (currencyValue === null || currencyValue === undefined) {
            return null
        }

        // Defensive: strip any currency symbol or thousands separator, so a
        // "$1,299.00" style response degrades to the right number rather than
        // silently falling back to the untested price. Shop is USD / en-US.
        var normalized = String(currencyValue).replace(/[^0-9.-]/g, '')
        var amount = parseFloat(normalized)

        return isNaN(amount) ? null : Math.round(amount * 100)
    }

    /**
     * Price Intelligems wants us to charge for this variant, in cents.
     *
     * Falls back to the theme's own price (`fallbackCents`) whenever we cannot
     * get a tested price, which covers all of:
     *   - the Intelligems script never loaded      -> window.igData is undefined
     *   - the script loaded but the API is missing -> igData.price is undefined
     *   - no test is running, or this product is
     *     not part of the running test             -> the API returns null
     */
    window.igGetVariantPriceCents = function (variantId, fallbackCents) {
        var fallback = Number(fallbackCents) || 0

        if (!variantId) {
            return fallback
        }

        var testedPrice = null

        try {
            testedPrice = window.igData?.price?.getPriceByVariantId?.(variantId)
        } catch (e) {
            return fallback
        }

        var cents = toCents(testedPrice)

        // No tested price for this variant — use the theme's original price.
        return cents === null ? fallback : cents
    }

    /**
     * Full subtotal for one element: tested variant price + selected add-ons, in cents.
     */
    window.igGetSubtotalCents = function (el) {
        if (!el) {
            return 0
        }

        var base = window.igGetVariantPriceCents(
            el.dataset.variantId,
            el.dataset.currentVariantPrice
        )

        return base + (Number(el.dataset.upsellsTotalPrice) || 0)
    }

    /**
     * Re-render every personalization subtotal on the page.
     */
    window.igUpdateCustomSubtotals = function (root) {
        if (typeof window.formatMoney !== 'function') {
            return
        }

        var scope = root || document

        scope.querySelectorAll(SUBTOTAL_SELECTOR).forEach(function (el) {
            el.textContent = window.formatMoney(window.igGetSubtotalCents(el))
        })
    }

    function refresh () {
        window.igUpdateCustomSubtotals()
    }

    function refreshWhenDomReady () {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', refresh, { once: true })
        } else {
            refresh()
        }
    }

    // `onIgReady` fires immediately if Intelligems has already loaded, so this
    // covers both orderings. Preserve any callbacks another script registered.
    var existing = window.onIgReady
    var callbacks = []

    if (Array.isArray(existing)) {
        callbacks = existing.slice()
    } else if (typeof existing === 'function') {
        callbacks = [existing]
    }

    callbacks.push(refreshWhenDomReady)
    window.onIgReady = callbacks
})()
